import pc from 'picocolors';

export enum ExitCodes {
  SUCCESS = 0,
  GENERAL_ERROR = 1,
  MISUSE = 2,
  SIGINT = 130,
  SIGTERM = 143,
}

export interface ErrorContext {
  command?: string;
  operation?: string;
  file?: string;
  suggestion?: string;
}

export class CLIError extends Error {
  public readonly exitCode: number;
  public readonly context: ErrorContext;

  constructor(message: string, exitCode = ExitCodes.GENERAL_ERROR, context: ErrorContext = {}) {
    super(message);
    this.name = 'CLIError';
    this.exitCode = exitCode;
    this.context = context;
  }
}

export function formatError(error: Error | CLIError, verbose = false): string {
  const lines: string[] = [];
  
  // Main error message
  lines.push(pc.red(`✗ ${error.message}`));
  
  // Context information
  if (error instanceof CLIError) {
    if (error.context.command) {
      lines.push(pc.dim(`  Command: ${error.context.command}`));
    }
    if (error.context.operation) {
      lines.push(pc.dim(`  Operation: ${error.context.operation}`));
    }
    if (error.context.file) {
      lines.push(pc.dim(`  File: ${error.context.file}`));
    }
    if (error.context.suggestion) {
      lines.push('');
      lines.push(pc.yellow(`💡 ${error.context.suggestion}`));
    }
  }
  
  // Stack trace in verbose mode
  if (verbose && error.stack) {
    lines.push('');
    lines.push(pc.dim('Stack trace:'));
    lines.push(pc.dim(error.stack));
  }
  
  return lines.join('\\n');
}

export function handleError(error: Error | CLIError, verbose = false): never {
  console.error('\\n' + formatError(error, verbose));
  
  const exitCode = error instanceof CLIError ? error.exitCode : ExitCodes.GENERAL_ERROR;
  process.exit(exitCode);
}

export function createCLIError(
  message: string, 
  context: ErrorContext = {}, 
  exitCode = ExitCodes.GENERAL_ERROR
): CLIError {
  return new CLIError(message, exitCode, context);
}

// Common error creators
export const createValidationError = (message: string, context: ErrorContext = {}) =>
  createCLIError(message, context, ExitCodes.MISUSE);

export const createFileError = (message: string, file: string, suggestion?: string) =>
  createCLIError(message, { file, suggestion });

export const createCommandError = (message: string, command: string, suggestion?: string) =>
  createCLIError(message, { command, suggestion });